import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";

const today = new Date();

export function Charts() {
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [hours, setHours] = useState([]);
  const [fleet, setFleet] = useState([]);
  const [regii, setRegii] = useState([]);
  const [topParts, setTopParts] = useState([]);
  const [loading, setLoading] = useState(false);

  const monthLabel = (ym) => {
    if (!ym) return "";
    const [y, m] = ym.split("-");
    const names = ["Ian","Feb","Mar","Apr","Mai","Iun","Iul","Aug","Sep","Oct","Noi","Dec"];
    return `${names[parseInt(m, 10) - 1]} ${y}`;
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get(`/charts/hours-by-person?year=${year}&month=${month}`).catch(() => ({ data: [] })),
      api.get(`/charts/fleet-consumption?months=6`).catch(() => ({ data: [] })),
      api.get(`/charts/regii-by-month?months=6`).catch(() => ({ data: [] })),
      api.get(`/charts/top-consumed-parts?limit=10`).catch(() => ({ data: [] })),
    ]).then(([h, f, r, t]) => {
      setHours(h.data || []);
      setFleet((f.data || []).map((row) => ({ ...row, label: monthLabel(row.month) })));
      setRegii((r.data || []).map((row) => ({ ...row, label: monthLabel(row.month) })));
      setTopParts(t.data || []);
    }).finally(() => setLoading(false));
  }, [year, month]);

  const totalHours = hours.reduce((s, r) => s + (r.total_hours || 0), 0);
  const avgConsumption = fleet.length ? (fleet.reduce((s, r) => s + r.avg_consumption, 0) / fleet.length).toFixed(2) : "—";
  const regieKeys = useMemo(() => {
    const set = new Set();
    regii.forEach((row) => Object.keys(row).forEach((k) => { if (k !== "month" && k !== "label") set.add(k); }));
    return Array.from(set);
  }, [regii]);
  const REGIE_COLORS = ["#0044FF", "#FF8A00", "#10B981", "#A855F7", "#EF4444", "#06B6D4", "#FACC15", "#F97316"];

  return (
    <div className="space-y-6" data-testid="charts-view">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="card-flat p-4">
          <Label className="small-label">Lună</Label>
          <Select value={String(month)} onValueChange={(v) => setMonth(parseInt(v, 10))}>
            <SelectTrigger className="mt-1"><SelectValue/></SelectTrigger>
            <SelectContent>
              {[1,2,3,4,5,6,7,8,9,10,11,12].map((m) => (
                <SelectItem key={m} value={String(m)}>{["Ianuarie","Februarie","Martie","Aprilie","Mai","Iunie","Iulie","August","Septembrie","Octombrie","Noiembrie","Decembrie"][m-1]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="card-flat p-4">
          <Label className="small-label">An</Label>
          <Input type="number" value={year} onChange={(e) => setYear(parseInt(e.target.value, 10) || year)} className="mt-1"/>
        </div>
        <div className="card-flat p-4">
          <Label className="small-label">Sumar</Label>
          <div className="text-sm mt-2 space-y-1">
            <div>Total ore lucrate: <strong className="text-[var(--primary)]">{totalHours.toFixed(1)} h</strong></div>
            <div>Consum mediu flotă: <strong className="text-[#FF8A00]">{avgConsumption} L/100km</strong></div>
          </div>
        </div>
      </div>

      <div className="card-flat p-5" data-testid="chart-hours">
        <h3 className="font-display text-lg font-bold mb-1">Ore lucrate per persoană</h3>
        <p className="text-xs text-zinc-500 mb-4">Total ore aprobate pe luna {String(month).padStart(2,"0")}/{year}</p>
        {loading ? (
          <div className="text-sm text-zinc-400 py-12 text-center">Se încarcă…</div>
        ) : hours.length === 0 ? (
          <div className="text-sm text-zinc-400 py-12 text-center italic">Nicio înregistrare în luna selectată.</div>
        ) : (
          <ResponsiveContainer width="100%" height={Math.max(220, hours.length * 36)}>
            <BarChart data={hours} layout="vertical" margin={{ left: 80, right: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" horizontal={false}/>
              <XAxis type="number" tick={{ fontSize: 11 }}/>
              <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11 }}/>
              <Tooltip
                cursor={{ fill: "rgba(0,68,255,0.05)" }}
                contentStyle={{ borderRadius: 8, border: "1px solid #E4E4E7", fontSize: 12 }}
                formatter={(v, _, p) => [`${v} h · ${p.payload.days} zile`, "Total"]}
              />
              <Bar dataKey="total_hours" radius={[0, 4, 4, 0]}>
                {hours.map((_, i) => <Cell key={i} fill="#0044FF"/>)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="card-flat p-5" data-testid="chart-fleet">
        <h3 className="font-display text-lg font-bold mb-1">Consum mediu flotă</h3>
        <p className="text-xs text-zinc-500 mb-4">Litri / 100 km pe ultimele 6 luni</p>
        {loading ? (
          <div className="text-sm text-zinc-400 py-12 text-center">Se încarcă…</div>
        ) : fleet.length === 0 ? (
          <div className="text-sm text-zinc-400 py-12 text-center italic">Date insuficiente — adaugă alimentări consecutive cu km pentru a calcula consumul.</div>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={fleet} margin={{ left: 8, right: 12, top: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7"/>
              <XAxis dataKey="label" tick={{ fontSize: 11 }}/>
              <YAxis tick={{ fontSize: 11 }} domain={[0, "auto"]} unit=" L"/>
              <Tooltip
                contentStyle={{ borderRadius: 8, border: "1px solid #E4E4E7", fontSize: 12 }}
                formatter={(v, _, p) => [`${v} L/100km · ${p.payload.samples} măsurători`, "Consum"]}
              />
              <Line type="monotone" dataKey="avg_consumption" stroke="#FF8A00" strokeWidth={2.5} dot={{ r: 4, fill: "#FF8A00" }} activeDot={{ r: 6 }}/>
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="card-flat p-5" data-testid="chart-regii">
        <h3 className="font-display text-lg font-bold mb-1">Regii — om-zile pe lună</h3>
        <p className="text-xs text-zinc-500 mb-4">Aproximare: oameni × 5 zile lucrătoare/săptămână</p>
        {loading ? (
          <div className="text-sm text-zinc-400 py-12 text-center">Se încarcă…</div>
        ) : regii.length === 0 ? (
          <div className="text-sm text-zinc-400 py-12 text-center italic">Nicio regie planificată în această perioadă.</div>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={regii} margin={{ left: 8, right: 12, top: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7"/>
              <XAxis dataKey="label" tick={{ fontSize: 11 }}/>
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E4E4E7", fontSize: 12 }}/>
              {regieKeys.map((k, i) => (
                <Bar key={k} dataKey={k} stackId="a" fill={REGIE_COLORS[i % REGIE_COLORS.length]} radius={i === regieKeys.length - 1 ? [4, 4, 0, 0] : 0}/>
              ))}
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="card-flat p-5" data-testid="chart-top-parts">
        <h3 className="font-display text-lg font-bold mb-1">Top piese / consumabile uzate</h3>
        <p className="text-xs text-zinc-500 mb-4">Numărul de operațiuni „used" din istoric</p>
        {loading ? (
          <div className="text-sm text-zinc-400 py-12 text-center">Se încarcă…</div>
        ) : topParts.length === 0 ? (
          <div className="text-sm text-zinc-400 py-12 text-center italic">Nicio reparație înregistrată încă.</div>
        ) : (
          <ResponsiveContainer width="100%" height={Math.max(220, topParts.length * 32)}>
            <BarChart data={topParts} layout="vertical" margin={{ left: 80, right: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" horizontal={false}/>
              <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false}/>
              <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 11 }}/>
              <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E4E4E7", fontSize: 12 }} formatter={(v) => [`${v} utilizări`, ""]}/>
              <Bar dataKey="uses" fill="#10B981" radius={[0, 4, 4, 0]}/>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
